import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";
import LandingNav from "../components/Layout/LandingNav";
import { TrainSourcePreview, AppearancePreview, AnalyticsPreview, InstallPreview } from "../components/marketing/ProductPreviews";
import LandingFooter from "../components/Layout/LandingFooter";
import {
    CheckCircle,
    ArrowRight,
    Shield,
    Sun,
    Moon,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    FileText,
    Globe,
    MessageSquare,
    MessageCircle,
    Home,
    Type,
    Check,
    Loader2,
    Upload,
    Settings,
    Code,
    Copy,
} from "lucide-react";
import { Toaster } from "react-hot-toast";
import { PLANS, annualMonthlyPrice, PRICE_RANGE } from "../config/plans";
import { PLATFORM_PAGES } from "../config/platformPages";
import { track, trackCta, trackPlanSelect, EVENTS } from "../utils/analytics";
import paperBrainzLogo from "../assets/PaperBrainz.png";
import paperBrainzWhiteLogo from "../assets/PaperBrainzWhiteLogo.png";
import dashboardShowcase from "../assets/dashboard.png";
import dashboardShowcaseMobile from "../assets/dashboard-mobile.png";
import hero from "../assets/hero.webp";
import showcaseHome from "../assets/showcase-home.webp";
import showcaseChat from "../assets/showcase-chat.webp";
import heroMobile from "../assets/hero-mobile.webp";

// The two beats of the visitor experience, in order. Keyed by translation prefix so
// copy stays in translations.js; the icons are the widget's own two tab icons.
const VISITOR_BEATS = [
    { key: "v1", Icon: Home, image: showcaseHome },
    { key: "v2", Icon: MessageCircle, image: showcaseChat },
];

function useScrollReveal() {
    const ref = React.useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(el);
                }
            },
            { threshold: 0.15 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return [ref, isVisible];
}

function AnimatedPrice({ value, d }) {
    const [display, setDisplay] = useState(value);
    const prevRef = React.useRef(value);

    useEffect(() => {
        const from = prevRef.current;
        const to = value;
        if (from === to) return;

        const duration = 400;
        const start = performance.now();

        const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            const current = from + (to - from) * eased;
            setDisplay(Math.round(current * 100) / 100);
            if (progress < 1) requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
        prevRef.current = to;
    }, [value]);

    const formatted = Number.isInteger(display) ? display : display.toFixed(2);
    return <>${formatted}</>;
}


function LandingPage() {
    const { user } = useAuth();
    const isLoggedIn = !!user;
    const { theme, toggleTheme: globalToggleTheme } = useTheme();
    const { lang, t, switchLang } = useLang();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [captionFade, setCaptionFade] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isAnnual, setIsAnnual] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.slice(1);
        requestAnimationFrame(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, [location.hash]);

    // Swipe detection
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }
    };

    const d = theme === "dark";
    const toggleTheme = globalToggleTheme;

    const heroRef = React.useRef(null);

    // Hero text reveal animation
    const [heroRevealed, setHeroRevealed] = useState(false);
    useEffect(() => {
        setTimeout(() => setHeroRevealed(true), 200);
    }, []);

    // Scroll-triggered fade-in animations
    const [howItWorksRef, howItWorksVisible] = useScrollReveal();
    const [visitorRef, visitorVisible] = useScrollReveal();
    const [featuresRef, featuresVisible] = useScrollReveal();
    const [showcaseRef, showcaseVisible] = useScrollReveal();
    const [engineRef, engineVisible] = useScrollReveal();
    const [pricingRef, pricingVisible] = useScrollReveal();
    const [faqRef, faqVisible] = useScrollReveal();
    const [ctaRef, ctaVisible] = useScrollReveal();

    // Visitor section: the pinned copy follows whichever photo is crossing the middle
    // of the viewport. The rootMargin collapses the root to a thin band at the centre,
    // so exactly one image is "intersecting" at a time and no scroll handler is needed.
    // The observed nodes live in the md-and-up branch, which is display:none on mobile —
    // there the copy sits under its own photo and activeBeat is never read.
    const [activeBeat, setActiveBeat] = useState(0);
    const visitorBeatRefs = React.useRef([]);
    useEffect(() => {
        const els = visitorBeatRefs.current.filter(Boolean);
        if (!els.length || typeof IntersectionObserver === "undefined") return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveBeat(Number(entry.target.dataset.beat));
                });
            },
            { rootMargin: "-45% 0px -45% 0px" },
        );
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // Reaching the pricing section is the strongest pre-signup intent signal we
    // have. useScrollReveal latches true once, so this fires at most once a visit.
    useEffect(() => {
        if (pricingVisible) track(EVENTS.PRICING_VIEW, { page: "landing" });
    }, [pricingVisible]);

    // PaperBrainz's own chatbot is embedded site-wide by the script tag in
    // public/index.html, so the best product demo is already on the page — it just had
    // nothing pointing at it. widget.js exposes no global open() API, but its launcher
    // button carries a stable id prefix, so we click that rather than modifying the
    // widget. If the widget failed to load (blocked, offline), fall back to email so
    // the CTA is never a dead end.
    const openDemoChat = useCallback(() => {
        track(EVENTS.DEMO_OPENED, { page: "landing" });
        const launcher = document.querySelector('[id^="paperbrain-chat-button-"]');
        if (launcher) {
            launcher.click();
            return;
        }
        window.location.href = "mailto:support@paperbrainz.com";
    }, []);

    // Prices, limits and the "popular" flag come from src/config/plans.js; only the
    // localized copy is looked up here. t() returns the key itself when a key is
    // missing, which is how we read a variable-length feature list per plan.
    const planFeatures = (slug) => {
        const features = [];
        for (let i = 1; ; i += 1) {
            const key = `pricing.${slug}.f${i}`;
            const value = t(key);
            if (value === key) break;
            features.push(value);
        }
        return features;
    };

    const pricingPlans = PLANS.map((plan) => {
        const footnoteKey = `pricing.${plan.slug}.footnote`;
        const footnote = t(footnoteKey);
        return {
            slug: plan.slug,
            name: t(`pricing.${plan.slug}.name`),
            monthlyPrice: plan.monthlyPrice,
            annualPrice: annualMonthlyPrice(plan),
            annualTotal: plan.annualTotal,
            description: t(`pricing.${plan.slug}.desc`),
            features: planFeatures(plan.slug),
            cta: t(`pricing.${plan.slug}.cta`),
            popular: plan.popular,
            footnote: footnote === footnoteKey ? null : footnote,
        };
    });

    // Homepage-only structured data. This used to live as static blocks in
    // public/index.html, which meant every prerendered route inherited the homepage's
    // FAQPage and Product schema — two conflicting FAQPage blocks per URL, one of them
    // describing questions not visible on that page. It belongs here, on the one page
    // that actually renders this content. Prices come from src/config/plans.js and the
    // questions from the same translation keys the FAQ section renders, so the markup
    // cannot drift from the visible page.
    const homeJsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "SoftwareApplication",
                name: "PaperBrainz",
                applicationCategory: "BusinessApplication",
                applicationSubCategory: "Customer Support Software",
                operatingSystem: "Web",
                description: t("seo.description"),
                url: "https://paperbrainz.com",
                screenshot: "https://paperbrainz.com/og-image.png",
                author: { "@type": "Organization", name: "Identie", url: "https://identie.net" },
                offers: {
                    "@type": "AggregateOffer",
                    lowPrice: String(PRICE_RANGE.low),
                    highPrice: String(PRICE_RANGE.high),
                    priceCurrency: "USD",
                    offerCount: String(PLANS.length),
                    availability: "https://schema.org/InStock",
                    url: "https://paperbrainz.com/pricing",
                },
            },
            {
                "@type": "FAQPage",
                mainEntity: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
                    "@type": "Question",
                    name: t(`faq.q${n}.q`),
                    acceptedAnswer: { "@type": "Answer", text: t(`faq.q${n}.a`) },
                })),
            },
            {
                "@type": "SiteNavigationElement",
                name: ["Pricing", "Security", ...PLATFORM_PAGES.map((p) => p.title)],
                url: [
                    "https://paperbrainz.com/pricing",
                    "https://paperbrainz.com/security",
                    ...PLATFORM_PAGES.map((p) => `https://paperbrainz.com${p.path}`),
                ],
            },
        ],
    };

    return (
        <div className={`min-h-screen transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            <SEO
                title={t("seo.title")}
                description={t("seo.description")}
                path={lang === "hy" ? "/hy" : "/"}
                lang={lang}
                alternates={[
                    { lang: "en", path: "/" },
                    { lang: "hy", path: "/hy" },
                ]}
                jsonLd={homeJsonLd}
            />
            <LandingNav isLandingPage />

            {/* Hero Section */}
            <section ref={heroRef} className={`relative px-4 sm:px-6 lg:px-8 overflow-hidden pt-32 pb-20 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div className="max-w-5xl mx-auto relative z-10 w-full">
                    {/* Centered text block */}
                    <div className="text-center max-w-3xl mx-auto">
                        <p
                            className={`text-sm font-medium tracking-wide mb-6 transition-all duration-500 ${heroRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${d ? "text-gray-400" : "text-gray-500"}`}
                        >
                            {t("hero.eyebrow")}
                        </p>

                        <h1
                            className={`${lang === "hy" ? "text-[34px] sm:text-[40px] md:text-[52px] lg:text-[60px]" : "text-[44px] sm:text-5xl md:text-6xl lg:text-[68px]"} font-semibold mb-7 leading-[1.05] tracking-[-0.02em] transition-all duration-500 ${heroRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${d ? "text-white" : "text-gray-900"}`}
                            style={{ transitionDelay: "80ms", textWrap: "balance" }}
                        >
                            {t("hero.title1")}<br className="hidden sm:block" /> {t("hero.title2")}
                        </h1>

                        <p
                            className={`text-lg md:text-xl mb-10 leading-relaxed max-w-2xl mx-auto transition-all duration-500 ${heroRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${d ? "text-gray-400" : "text-gray-600"}`}
                            style={{ transitionDelay: "160ms" }}
                        >
                            {t("hero.subtitle")}
                        </p>

                        <div
                            className={`flex flex-col sm:flex-row gap-3 justify-center items-center transition-all duration-500 ${heroRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                            style={{ transitionDelay: "240ms" }}
                        >
                            <Link
                                to={isLoggedIn ? "/dashboard" : "/register"}
                                onClick={() => trackCta("hero", isLoggedIn ? "go_to_dashboard" : "start_trial")}
                                className={`px-7 py-3.5 rounded-full transition-colors font-medium text-[15px] ${
                                    d ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-black"
                                }`}
                            >
                                {isLoggedIn ? t("hero.ctaPrimaryLoggedIn") : t("hero.ctaPrimary")}
                            </Link>
                            <button
                                onClick={() => {
                                    trackCta("hero", "try_demo");
                                    openDemoChat();
                                }}
                                className={`px-7 py-3.5 rounded-full transition-colors font-medium text-[15px] border ${
                                    d ? "text-white border-white/15 hover:bg-white/[0.04]" : "text-gray-900 border-gray-300 hover:bg-gray-50"
                                }`}
                            >
                                {t("hero.ctaSecondary")}
                            </button>
                        </div>

                        <p
                            className={`mt-6 text-sm transition-all duration-500 ${heroRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${d ? "text-gray-500" : "text-gray-500"}`}
                            style={{ transitionDelay: "320ms" }}
                        >
                            {t("hero.note")}
                        </p>
                    </div>

                    {/* Big product image: 1600×900 desktop, 1200×1500 portrait below 640px */}
                    <div
                        className={`mt-20 relative max-w-6xl mx-auto transition-all duration-700 ${heroRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                        style={{ transitionDelay: "400ms" }}
                    >
                        <div
                            className={`rounded-2xl overflow-hidden border ${
                                d ? "border-white/[0.08] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]" : "border-gray-200 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.15)]"
                            }`}
                        >
                            <picture>
                                <source media="(min-width: 640px)" srcSet={hero} />
                                <img
                                    src={heroMobile}
                                    alt={t("hero.imageAlt")}
                                    className="w-full h-auto block"
                                    draggable="false"
                                />
                            </picture>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust Bar */}
            <section className={`py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div className="max-w-5xl mx-auto">
                    <p className={`text-center text-xs uppercase tracking-[0.2em] mb-8 font-medium ${d ? "text-gray-600" : "text-gray-400"}`}>
                        {t("trust.label")}
                    </p>
                    <div className={`flex flex-wrap items-center justify-center gap-x-10 sm:gap-x-14 gap-y-6 ${d ? "text-gray-400" : "text-gray-500"}`}>
                        <div className="flex items-center gap-2.5">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M11.9 1.036c-.015-.986-1.26-1.41-1.874-.637L1.764 10.967c-.575.726-.06 1.79.864 1.79h7.275l.013 9.21c.014.987 1.259 1.41 1.873.637l8.262-10.568c.575-.726.06-1.79-.865-1.79l-7.275.001-.013-9.21Z"/></svg>
                            <span className="text-sm font-medium">{t("trust.supabase")}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.236l7.5 3.75v8.028L12 19.764l-7.5-3.75V7.986L12 4.236z"/></svg>
                            <span className="text-sm font-medium">{t("trust.qdrant")}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <Shield className="w-5 h-5" />
                            <span className="text-sm font-medium">{t("trust.ssl")}</span>
                        </div>
                        <div className="flex items-center gap-2.5">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M13.221 2.933c-.34-.69-1.32-.69-1.66 0l-2.4 4.866-5.37.78c-.762.111-1.067 1.046-.515 1.585l3.886 3.789-.917 5.347c-.13.76.668 1.339 1.349.978L12 17.722l4.806 2.526c.681.36 1.479-.218 1.349-.978l-.917-5.347 3.886-3.789c.552-.539.247-1.474-.515-1.585l-5.37-.78-2.4-4.866z"/></svg>
                            <span className="text-sm font-medium">{t("trust.uptime")}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* Stats Section */}
            <section className={`py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-center">
                        {/* Left: narrative */}
                        <div>
                            <p className={`text-xs uppercase tracking-[0.2em] mb-5 font-medium ${d ? "text-gray-600" : "text-gray-400"}`}>
                                {t("stats.eyebrow")}
                            </p>
                            <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold mb-6 tracking-tight leading-[1.1] [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                                {t("stats.title1")} <span className={d ? "text-gray-500" : "text-gray-500"}>{t("stats.title2")}</span>
                            </h2>
                            <p className={`text-lg leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                                {t("stats.subtitle")}
                            </p>
                        </div>

                        {/* Right: big stats stack */}
                        <div className={`divide-y ${d ? "divide-white/[0.06]" : "divide-gray-200"}`}>
                            {/* These figures must stay verifiable from the product itself.
                                Two invented percentages (70% / 53%) previously sat here with
                                no source behind them — do not reintroduce unsourced stats. */}
                            {[
                                {
                                    stat: "24/7",
                                    title: t("stats.s1.title"),
                                    desc: t("stats.s1.desc"),
                                },
                                {
                                    stat: "28",
                                    title: t("stats.s2.title"),
                                    desc: t("stats.s2.desc"),
                                },
                                {
                                    stat: "1",
                                    title: t("stats.s3.title"),
                                    desc: t("stats.s3.desc"),
                                },
                            ].map((item, i) => (
                                <div key={i} className="flex flex-col sm:grid sm:grid-cols-[auto_1fr] gap-3 sm:gap-x-8 sm:items-center py-6 sm:py-7 first:pt-0 last:pb-0">
                                    <div className={`text-[44px] sm:text-5xl md:text-6xl font-semibold tracking-tight tabular-nums leading-none ${d ? "text-white" : "text-gray-900"}`}>
                                        {item.stat}
                                    </div>
                                    <div>
                                        <h3 className={`text-base md:text-lg font-medium mb-1 ${d ? "text-white" : "text-gray-900"}`}>{item.title}</h3>
                                        <p className={`text-sm leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* How It Works Section */}
            <section id="how-it-works" className={`scroll-mt-28 py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={howItWorksRef}
                    className={`max-w-5xl mx-auto transition-all duration-700 ${howItWorksVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <div className="text-center mb-16">
                        <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold mb-4 tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                            {t("how.title")}
                        </h2>
                        <p className={`text-lg md:text-xl max-w-2xl mx-auto font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("how.subtitle")}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-6 md:gap-0 items-start">
                        {[
                            {
                                num: "01",
                                title: t("how.s1.title"),
                                desc: t("how.s1.desc"),
                            },
                            {
                                num: "02",
                                title: t("how.s2.title"),
                                desc: t("how.s2.desc"),
                            },
                            {
                                num: "03",
                                title: t("how.s3.title"),
                                desc: t("how.s3.desc"),
                            },
                        ].map((step, i) => (
                            <React.Fragment key={step.num}>
                                <div className="text-center px-4">
                                    <span className={`text-6xl font-extralight tracking-tight ${d ? "text-white/10" : "text-black/10"}`}>
                                        {step.num}
                                    </span>
                                    <h3 className={`text-xl md:text-2xl font-medium mt-4 mb-3 ${d ? "text-white" : "text-gray-900"}`}>{step.title}</h3>
                                    <p className={`text-base leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{step.desc}</p>
                                </div>
                                {i < 2 && (
                                    <>
                                        {/* Desktop: dashed arrow */}
                                        <div className="hidden md:flex items-center justify-center pt-8 px-2">
                                            <div className={`w-12 lg:w-16 border-t border-dashed ${d ? "border-white/[0.12]" : "border-gray-300"}`} />
                                            <svg
                                                className={`w-3 h-3 -ml-0.5 flex-shrink-0 ${d ? "text-white/20" : "text-gray-300"}`}
                                                fill="currentColor"
                                                viewBox="0 0 12 12"
                                            >
                                                <path d="M4.5 1.5l5 4.5-5 4.5V1.5z" />
                                            </svg>
                                        </div>
                                        {/* Mobile: vertical dashed line */}
                                        <div className={`md:hidden flex justify-center py-2`}>
                                            <div className={`h-8 border-l border-dashed ${d ? "border-white/[0.12]" : "border-gray-300"}`} />
                                        </div>
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* Visitor Experience — the only section that shows the widget in context on a real
                business instead of floating on a gradient, and the only one told from the
                visitor's side rather than the owner's.

                Pinned copy, images scrolling past. The two photos are NOT a matched pair — the
                home shot is a tight crop, the chat shot a wide one — so any layout that puts them
                adjacent renders the same widget at two visibly different sizes and reads as a
                mistake. Here only one is ever at the centre of the viewport, so they are never
                compared, and the column is narrow enough that the portrait recompositions (not
                the 16:9 ones) keep the widget large enough to actually read.

                The kickers are the widget's own two tabs, icons included: the section's two beats
                ARE Home and Chat. That is why there is no 01/02 here — the sequence is already
                named by the product, and How It Works directly above owns the numerals. The rule
                above the copy is the progress indicator; it grows on the active beat.

                Below md the pin is dropped entirely and each photo simply carries its own copy —
                sticky columns and short viewports do not mix. */}
            <section className={`py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={visitorRef}
                    className={`max-w-6xl mx-auto transition-all duration-700 ${visitorVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    {/* Desktop: copy pins on the left, photos scroll past on the right */}
                    <div className="hidden md:grid md:grid-cols-12 md:gap-x-8 items-start">
                        <div className="md:col-span-4 md:sticky md:top-28">
                            <h2 className={`text-4xl lg:text-[3.25rem] font-semibold tracking-tight leading-[1.05] mb-5 [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                                {t("visitor.title")}
                            </h2>
                            <p className={`text-lg leading-relaxed font-light mb-12 ${d ? "text-gray-500" : "text-gray-500"}`}>
                                {t("visitor.subtitle")}
                            </p>

                            {/* Progress: one rule per beat, the active one twice as long */}
                            <div className="flex items-center gap-2 mb-7" aria-hidden="true">
                                {VISITOR_BEATS.map((beat, i) => (
                                    <span
                                        key={beat.key}
                                        className={`h-px transition-all duration-500 motion-reduce:transition-none ${
                                            i === activeBeat
                                                ? `w-10 ${d ? "bg-white" : "bg-gray-900"}`
                                                : `w-5 ${d ? "bg-white/20" : "bg-gray-300"}`
                                        }`}
                                    />
                                ))}
                            </div>

                            {/* Both beats stacked in one grid cell so the swap is a crossfade
                                with no height jump between differing copy lengths. */}
                            <div className="grid">
                                {VISITOR_BEATS.map((beat, i) => (
                                    <div
                                        key={beat.key}
                                        className={`col-start-1 row-start-1 transition-opacity duration-500 motion-reduce:transition-none ${
                                            i === activeBeat ? "opacity-100" : "opacity-0"
                                        }`}
                                        aria-hidden={i !== activeBeat}
                                    >
                                        <p className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium mb-3 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                            <beat.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                                            {t(`visitor.${beat.key}.kicker`)}
                                        </p>
                                        <h3 className={`text-2xl lg:text-3xl font-medium tracking-tight mb-3 ${d ? "text-white" : "text-gray-900"}`}>
                                            {t(`visitor.${beat.key}.title`)}
                                        </h3>
                                        <p className={`text-base leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                                            {t(`visitor.${beat.key}.desc`)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="md:col-start-7 md:col-span-6 space-y-24 lg:space-y-32">
                            {VISITOR_BEATS.map((beat, i) => (
                                <img
                                    key={beat.key}
                                    ref={(el) => { visitorBeatRefs.current[i] = el; }}
                                    data-beat={i}
                                    src={beat.image}
                                    alt={t(`visitor.${beat.key}.imageAlt`)}
                                    className="w-full aspect-[4/5] object-cover rounded-[20px] select-none"
                                    loading="lazy"
                                    draggable="false"
                                />
                            ))}
                        </div>
                    </div>

                    {/* Mobile: no pin — each photo carries its own copy */}
                    <div className="md:hidden">
                        <h2 className={`text-4xl font-semibold tracking-tight leading-[1.05] mb-5 [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                            {t("visitor.title")}
                        </h2>
                        <p className={`text-lg leading-relaxed font-light mb-12 ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("visitor.subtitle")}
                        </p>
                        <div className="space-y-14">
                            {VISITOR_BEATS.map((beat) => (
                                <figure key={beat.key} className="m-0">
                                    <img
                                        src={beat.image}
                                        alt={t(`visitor.${beat.key}.imageAlt`)}
                                        className="w-full aspect-[4/5] object-cover rounded-[20px] select-none"
                                        loading="lazy"
                                        draggable="false"
                                    />
                                    <figcaption className={`mt-7 pt-6 border-t ${d ? "border-white/[0.1]" : "border-gray-200"}`}>
                                        <p className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-medium mb-3 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                            <beat.Icon className="w-3.5 h-3.5" strokeWidth={2} />
                                            {t(`visitor.${beat.key}.kicker`)}
                                        </p>
                                        <h3 className={`text-2xl font-medium tracking-tight mb-3 ${d ? "text-white" : "text-gray-900"}`}>
                                            {t(`visitor.${beat.key}.title`)}
                                        </h3>
                                        <p className={`text-base leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                                            {t(`visitor.${beat.key}.desc`)}
                                        </p>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* Features Section */}
            <section id="features" className={`scroll-mt-28 py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={featuresRef}
                    className={`max-w-5xl mx-auto transition-all duration-700 ${featuresVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <div className="text-center mb-16">
                        <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold mb-4 tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>{t("features.title")}</h2>
                        <p className={`text-lg md:text-xl max-w-2xl mx-auto font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("features.subtitle")}
                        </p>
                    </div>

                    {(() => {
                        const cards = [
                            {
                                preview: <TrainSourcePreview d={d} />,
                                title: t("features.f1.title"),
                                description: t("features.f1.desc"),
                            },
                            {
                                preview: <AppearancePreview d={d} />,
                                title: t("features.f2.title"),
                                description: t("features.f2.desc"),
                            },
                            {
                                preview: <AnalyticsPreview d={d} />,
                                title: t("features.f3.title"),
                                description: t("features.f3.desc"),
                            },
                            {
                                preview: <InstallPreview d={d} />,
                                title: t("features.f4.title"),
                                description: t("features.f4.desc"),
                            },
                        ];
                        const renderCard = (card, key) => (
                            <div
                                key={key}
                                className={`group flex flex-col rounded-[20px] border overflow-hidden transition-all ${
                                    d ? "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]" : "bg-gray-50 border-gray-200 hover:shadow-sm"
                                }`}
                            >
                                <div className={`hidden md:block aspect-[4/3] overflow-hidden ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                                    {card.preview}
                                </div>
                                <div className="p-6 sm:p-7 md:p-8">
                                    <h3 className={`md:hidden text-xl font-semibold mb-3 tracking-tight ${d ? "text-white" : "text-gray-900"}`}>
                                        {card.title}
                                    </h3>
                                    <p className={`text-base leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                                        {card.description}
                                    </p>
                                </div>
                            </div>
                        );
                        return (
                            <>
                                <div className="grid md:grid-cols-2 gap-5">
                                    {/* Left column */}
                                    <div className="space-y-5">
                                        {renderCard(cards[0], 0)}
                                        {renderCard(cards[2], 2)}
                                    </div>
                                    {/* Right column, offset down */}
                                    <div className="space-y-5 md:mt-20">
                                        {renderCard(cards[1], 1)}
                                        {renderCard(cards[3], 3)}
                                    </div>
                                </div>
                                {/* Desktop-only, like the previews it refers to. */}
                                <p className={`hidden md:block text-center text-xs mt-8 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                    {t("features.mockNote")}
                                </p>
                            </>
                        );
                    })()}
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* Dashboard Showcase Section */}
            <section className={`py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={showcaseRef}
                    className={`max-w-5xl mx-auto transition-all duration-700 ${showcaseVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <div className="text-center mb-14">
                        <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold mb-4 tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                            {t("showcase.title")}
                        </h2>
                        <p className={`text-lg md:text-xl max-w-2xl mx-auto font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("showcase.subtitle")}
                        </p>
                    </div>

                    {/* Floating showcase: full-width dashboard with chat widget overlapping bottom-right */}
                    <div className="relative">
                        {/* Dashboard (full width, browser chrome) */}
                        <div
                            className={`rounded-[20px] border overflow-hidden transition-colors duration-300 ${
                                d ? "bg-[#111] border-white/[0.06] shadow-2xl shadow-black/40" : "bg-white border-gray-200 shadow-xl shadow-gray-900/5"
                            }`}
                        >
                            {/* Browser Chrome */}
                            <div className={`flex items-center justify-between px-3 sm:px-5 py-2.5 sm:py-3 border-b ${d ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}>
                                <div className="flex items-center space-x-1.5 sm:space-x-2">
                                    <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${d ? "bg-white/10" : "bg-black/10"}`}></div>
                                    <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${d ? "bg-white/10" : "bg-black/10"}`}></div>
                                    <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${d ? "bg-white/10" : "bg-black/10"}`}></div>
                                </div>
                                <div className={`flex-1 max-w-xs mx-2 sm:mx-4 px-2 sm:px-4 py-1 rounded-full text-[10px] sm:text-xs text-center truncate ${d ? "bg-white/[0.04] text-gray-600" : "bg-gray-100 text-gray-400"}`}>
                                    paperbrainz.com/dashboard
                                </div>
                                <div className="w-8 sm:w-16"></div>
                            </div>
                            <picture>
                                <source media="(min-width: 640px)" srcSet={dashboardShowcase} />
                                <img
                                    src={dashboardShowcaseMobile}
                                    alt={t("showcase.imageAlt")}
                                    className="w-full aspect-[3/4] sm:aspect-[16/9] object-cover object-top select-none"
                                    loading="lazy"
                                    draggable="false"
                                />
                            </picture>
                        </div>

                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* Price Comparison Section */}
            <section className={`py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={engineRef}
                    className={`max-w-5xl mx-auto transition-all duration-700 ${engineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    {/* Heading */}
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold mb-5 tracking-tight leading-[1.1] [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                            {t("compare.title1")}<br className="hidden sm:block" /> {t("compare.title2")}
                        </h2>
                        <p className={`text-lg leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("compare.subtitle")}
                        </p>
                    </div>

                    {/* Comparison card */}
                    <div className={`rounded-[24px] border p-6 sm:p-10 md:p-14 ${d ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}>
                        <p className={`text-xs uppercase tracking-[0.18em] font-medium mb-2 ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("compare.eyebrow")}
                        </p>
                        <p className={`text-sm font-light mb-8 sm:mb-10 ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("compare.note")}
                        </p>

                        {(() => {
                            const options = [
                                {
                                    label: t("compare.opt1.label"),
                                    sub: t("compare.opt1.sub"),
                                    value: 3000,
                                    display: "~$3,000",
                                },
                                {
                                    label: t("compare.opt2.label"),
                                    sub: t("compare.opt2.sub"),
                                    value: 12000,
                                    display: "~$12,000+",
                                },
                                {
                                    label: t("compare.opt3.label"),
                                    sub: t("compare.opt3.sub"),
                                    value: PRICE_RANGE.low,
                                    display: `$${PRICE_RANGE.low}`,
                                    winner: true,
                                },
                            ];
                            const max = 13000;
                            const ticks = [12000, 6000, 0];

                            return (
                                <>
                                    {/* Mobile: horizontal list */}
                                    <div className="md:hidden space-y-4 sm:space-y-5">
                                        {options.map((opt, i) => (
                                            <div key={opt.label}>
                                                <div className="flex items-baseline justify-between gap-3 mb-2">
                                                    <div className="min-w-0">
                                                        <div className={`text-sm sm:text-base font-semibold ${
                                                            opt.winner ? (d ? "text-white" : "text-gray-900") : d ? "text-gray-300" : "text-gray-700"
                                                        }`}>
                                                            {opt.label}
                                                        </div>
                                                        <div className={`text-xs sm:text-[13px] font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                                                            {opt.sub}
                                                        </div>
                                                    </div>
                                                    <div className={`text-xl sm:text-2xl font-semibold tabular-nums whitespace-nowrap shrink-0 ${
                                                        opt.winner ? (d ? "text-white" : "text-gray-900") : d ? "text-gray-400" : "text-gray-600"
                                                    }`}>
                                                        {opt.display}
                                                        <span className={`text-xs sm:text-sm font-light ml-1 ${d ? "text-gray-500" : "text-gray-500"}`}>{t("compare.permo")}</span>
                                                    </div>
                                                </div>
                                                <div className={`h-2 sm:h-2.5 rounded-full overflow-hidden ${d ? "bg-white/[0.04]" : "bg-gray-200/70"}`}>
                                                    <div
                                                        className={`h-full rounded-full transition-[width] ease-out ${
                                                            opt.winner
                                                                ? d ? "bg-white" : "bg-gray-900"
                                                                : d ? "bg-white/25" : "bg-gray-400"
                                                        }`}
                                                        style={{
                                                            width: engineVisible
                                                                ? `${Math.max((opt.value / max) * 100, opt.winner ? 1.2 : 0)}%`
                                                                : "0%",
                                                            transitionDuration: "1100ms",
                                                            transitionDelay: `${200 + i * 150}ms`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Desktop: vertical bars + pill labels */}
                                    <div className="hidden md:block">
                                        <div className="relative h-[360px] pl-14">
                                            {/* Y-axis ticks + dashed grid lines */}
                                            {ticks.map((t) => (
                                                <div
                                                    key={t}
                                                    className="absolute left-0 right-0 flex items-center"
                                                    style={{ bottom: `${(t / max) * 100}%`, transform: "translateY(50%)" }}
                                                >
                                                    <span className={`text-[11px] font-light w-12 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                                        {t === 0 ? "$0" : `$${t / 1000}k`}
                                                    </span>
                                                    <div
                                                        className={`flex-1 border-t border-dashed ${d ? "border-white/[0.08]" : "border-gray-300/70"}`}
                                                    />
                                                </div>
                                            ))}

                                            {/* Bars row */}
                                            <div className="absolute inset-0 pl-14 flex items-end justify-around gap-10">
                                                {options.map((opt, i) => (
                                                    <div key={opt.label} className="flex-1 flex flex-col items-center justify-end h-full relative">
                                                        {/* Value label above bar */}
                                                        <span
                                                            className={`absolute text-xl font-semibold tabular-nums whitespace-nowrap transition-opacity duration-500 ${
                                                                opt.winner ? (d ? "text-white" : "text-gray-900") : d ? "text-gray-400" : "text-gray-600"
                                                            } ${engineVisible ? "opacity-100" : "opacity-0"}`}
                                                            style={{
                                                                bottom: `${Math.max((opt.value / max) * 100, opt.winner ? 2.5 : 0)}%`,
                                                                transform: "translateY(-12px)",
                                                                transitionDelay: `${600 + i * 120}ms`,
                                                            }}
                                                        >
                                                            {opt.display}
                                                        </span>
                                                        {/* Bar */}
                                                        <div
                                                            className={`w-16 rounded-t-md transition-[height] ease-out ${
                                                                opt.winner
                                                                    ? d ? "bg-white" : "bg-gray-900"
                                                                    : d ? "bg-white/20" : "bg-gray-300"
                                                            }`}
                                                            style={{
                                                                height: engineVisible
                                                                    ? `${Math.max((opt.value / max) * 100, opt.winner ? 2.5 : 0)}%`
                                                                    : "0%",
                                                                transitionDuration: "1100ms",
                                                                transitionDelay: `${200 + i * 120}ms`,
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* X-axis pill labels + sub-description */}
                                        <div className="pl-14 flex items-start justify-around gap-10 mt-5">
                                            {options.map((opt) => (
                                                <div key={opt.label} className="flex-1 flex flex-col items-center text-center gap-2">
                                                    <span
                                                        className={`px-4 py-1.5 rounded-full text-[13px] font-medium border whitespace-nowrap ${
                                                            opt.winner
                                                                ? d ? "bg-white text-black border-white" : "bg-gray-900 text-white border-gray-900"
                                                                : d ? "bg-white/[0.04] text-gray-400 border-white/[0.08]" : "bg-white text-gray-700 border-gray-200"
                                                        }`}
                                                    >
                                                        {opt.label}
                                                    </span>
                                                    <span className={`text-xs font-light leading-snug max-w-[200px] ${d ? "text-gray-500" : "text-gray-500"}`}>
                                                        {opt.sub}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            );
                        })()}

                        {/* A "53% of shoppers abandon…  $2,000 left on the table … pays for
                            itself ~130× over" callout used to sit here. Both the percentage and
                            the ROI multiplier were invented. Removed — do not restore without
                            a real, citable source. */}

                        <p className={`text-[11px] font-light mt-8 pt-5 border-t text-center max-w-3xl mx-auto ${d ? "text-gray-600 border-white/[0.06]" : "text-gray-400 border-gray-200"}`}>
                            {t("compare.footnote")}
                        </p>
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* Pricing Section */}
            <section id="pricing" className={`scroll-mt-28 py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={pricingRef}
                    className={`max-w-6xl mx-auto transition-all duration-700 ${pricingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <div className="text-center mb-16">
                        <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold mb-4 tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                            {t("pricing.title")}
                        </h2>
                        <p className={`text-lg md:text-xl max-w-2xl mx-auto font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                            {t("pricing.subtitle")}
                        </p>

                        {/* Billing Toggle */}
                        <div className="flex items-center justify-center mt-8">
                            <div
                                className={`relative flex items-center rounded-full p-1 border ${d ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-100 border-gray-200"}`}
                            >
                                <button
                                    onClick={() => setIsAnnual(false)}
                                    className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                        !isAnnual ? (d ? "bg-white text-black" : "bg-black text-white") : d ? "text-gray-400" : "text-gray-500"
                                    }`}
                                >
                                    {t("pricing.monthly")}
                                </button>
                                <div className="relative">
                                    <span className={`absolute -top-3 right-0 translate-x-1/3 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap z-20 ${d ? "bg-white text-black" : "bg-gray-900 text-white"}`}>
                                        {t("pricing.save")}
                                    </span>
                                    <button
                                        onClick={() => setIsAnnual(true)}
                                        className={`relative z-10 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                            isAnnual ? (d ? "bg-white text-black" : "bg-black text-white") : d ? "text-gray-400" : "text-gray-500"
                                        }`}
                                    >
                                        {t("pricing.annual")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {pricingPlans.map((plan, index) => (
                            <div
                                key={index}
                                className={`relative p-6 sm:p-8 rounded-[20px] border transition-all flex flex-col h-full ${
                                    plan.popular
                                        ? d
                                            ? "border-white/30 bg-white/[0.04] shadow-[0_20px_60px_-20px_rgba(255,255,255,0.08)]"
                                            : "border-gray-900 bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]"
                                        : d
                                          ? "border-white/[0.06] bg-white/[0.02]"
                                          : "border-gray-200 bg-white"
                                }`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                                        <span
                                            className={`px-4 py-1.5 rounded-full text-xs font-medium ${d ? "bg-white text-black" : "bg-black text-white"}`}
                                        >
                                            {t("pricing.popular")}
                                        </span>
                                    </div>
                                )}

                                <div className="mb-8">
                                    <h3 className={`text-lg font-medium mb-1 ${d ? "text-white" : "text-gray-900"}`}>{plan.name}</h3>
                                    <p className={`mb-5 text-sm font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{plan.description}</p>
                                    <div className="flex items-baseline">
                                        <span className={`text-5xl font-semibold tracking-tight tabular-nums ${d ? "text-white" : "text-gray-900"}`}>
                                            <AnimatedPrice value={isAnnual ? plan.annualPrice : plan.monthlyPrice} d={d} />
                                        </span>
                                        <span className={`ml-2 text-sm font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{t("pricing.perMonth")}</span>
                                    </div>
                                    {isAnnual && (
                                        <p className={`text-sm font-light mt-1 ${d ? "text-gray-600" : "text-gray-400"}`}>
                                            ${plan.annualTotal} {t("pricing.billedAnnually")}
                                        </p>
                                    )}
                                </div>

                                <ul className="space-y-3 mb-8">
                                    {plan.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} className="flex items-start space-x-3">
                                            <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${d ? "text-gray-400" : "text-gray-500"}`} />
                                            <span className={`text-sm font-light ${d ? "text-gray-400" : "text-gray-600"}`}>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-auto">
                                    {plan.footnote && (
                                        <p className={`text-xs font-light text-center mb-3 ${d ? "text-gray-500" : "text-gray-500"}`}>
                                            {plan.footnote}
                                        </p>
                                    )}
                                    <button
                                        onClick={() => {
                                            const planSlug = plan.slug;
                                            const billing = isAnnual ? "annual" : "monthly";
                                            trackPlanSelect(planSlug, billing);
                                            if (user) {
                                                navigate(`/dashboard/billing?autoCheckout=${planSlug}&billing=${billing}`);
                                            } else {
                                                navigate(`/register?plan=${planSlug}&billing=${billing}`);
                                            }
                                        }}
                                        className={`w-full py-3 rounded-full font-medium text-sm transition-all ${
                                            plan.popular
                                                ? d
                                                    ? "bg-white text-black hover:bg-gray-200"
                                                    : "bg-black text-white hover:bg-gray-800"
                                                : d
                                                  ? "text-white border border-white/[0.08] hover:bg-white/[0.04]"
                                                  : "text-gray-900 border border-gray-200 hover:bg-gray-50"
                                        }`}
                                    >
                                        {plan.cta}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={`mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                        <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${d ? "text-gray-500" : "text-gray-500"}`} />
                            <span>{t("pricing.bullet1")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${d ? "text-gray-500" : "text-gray-500"}`} />
                            <span>{t("pricing.bullet2")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${d ? "text-gray-500" : "text-gray-500"}`} />
                            <span>{t("pricing.bullet3")}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className={`w-4 h-4 ${d ? "text-gray-500" : "text-gray-500"}`} />
                            <span>{t("pricing.bullet4")}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* FAQ Section */}
            <section id="faq" className={`scroll-mt-28 py-20 md:py-28 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={faqRef}
                    className={`max-w-2xl mx-auto transition-all duration-700 ${faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <div className="text-center mb-14">
                        <h2 className={`text-4xl md:text-5xl lg:text-[3.25rem] font-semibold tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>{t("faq.title")}</h2>
                    </div>

                    <div className="space-y-3">
                        {[
                            { q: t("faq.q1.q"), a: t("faq.q1.a") },
                            { q: t("faq.q2.q"), a: t("faq.q2.a") },
                            { q: t("faq.q3.q"), a: t("faq.q3.a") },
                            { q: t("faq.q4.q"), a: t("faq.q4.a") },
                            { q: t("faq.q5.q"), a: t("faq.q5.a") },
                            { q: t("faq.q6.q"), a: t("faq.q6.a") },
                            { q: t("faq.q7.q"), a: t("faq.q7.a") },
                            { q: t("faq.q8.q"), a: t("faq.q8.a") },
                        ].map((faq, index) => (
                            <div
                                key={index}
                                className={`rounded-[16px] overflow-hidden border transition-colors ${
                                    d ? "border-white/[0.06] hover:bg-white/[0.02]" : "border-gray-200 hover:bg-gray-50"
                                }`}
                            >
                                <button
                                    onClick={() => {
                                        if (openFaq !== index) track(EVENTS.FAQ_OPEN, { question_index: index + 1 });
                                        setOpenFaq(openFaq === index ? null : index);
                                    }}
                                    className="w-full flex items-center justify-between p-5 text-left"
                                >
                                    <span className={`font-medium pr-4 text-[15px] ${d ? "text-white" : "text-gray-900"}`}>{faq.q}</span>
                                    <ChevronDown
                                        className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                                            openFaq === index ? "rotate-180" : ""
                                        } ${d ? "text-gray-600" : "text-gray-500"}`}
                                    />
                                </button>
                                <div className={`overflow-hidden transition-all duration-200 ${openFaq === index ? "max-h-72 pb-5" : "max-h-0"}`}>
                                    <p className={`px-5 text-sm leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{faq.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Divider */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div
                    className={`h-px ${d ? "bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"}`}
                />
            </div>

            {/* CTA Section */}
            <section className={`relative py-20 md:py-28 px-4 sm:px-6 lg:px-8 ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
                <div
                    ref={ctaRef}
                    className={`max-w-3xl mx-auto text-center relative z-10 transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                >
                    <h2 className={`text-4xl md:text-5xl lg:text-6xl font-semibold mb-6 tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                        {t("cta.title1")}<br />
                        <span className={d ? "text-gray-400" : "text-gray-500"}>{t("cta.title2")}</span>
                    </h2>
                    <p className={`text-lg md:text-xl mb-10 font-light ${d ? "text-gray-500" : "text-gray-500"}`}>
                        {t("cta.subtitle")}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <Link
                            to={isLoggedIn ? "/dashboard" : "/register"}
                            onClick={() => trackCta("final_cta", isLoggedIn ? "go_to_dashboard" : "start_trial")}
                            className={`group px-8 py-4 rounded-full transition-all font-medium text-base flex items-center space-x-2 ${
                                d ? "bg-white text-black hover:bg-gray-100" : "bg-black text-white hover:bg-gray-800"
                            }`}
                        >
                            <span>{isLoggedIn ? t("cta.primaryLoggedIn") : t("cta.primary")}</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <button
                            onClick={() => {
                                trackCta("final_cta", "compare_plans");
                                document.getElementById("pricing").scrollIntoView({ behavior: "smooth" });
                            }}
                            className={`px-8 py-4 rounded-full transition-all font-medium text-base border ${
                                d ? "text-gray-300 border-white/[0.08] hover:bg-white/[0.04]" : "text-gray-700 border-gray-200 hover:bg-gray-50"
                            }`}
                        >
                            {t("cta.secondary")}
                        </button>
                    </div>
                </div>
            </section>

            <Toaster
                position="top-center"
                toastOptions={{
                    style: d
                        ? { background: "#111", color: "#fff", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "16px" }
                        : { background: "#fff", color: "#000", border: "1px solid rgba(0,0,0,0.06)", borderRadius: "16px" },
                }}
            />

            <LandingFooter isLandingPage />
        </div>
    );
}

export default LandingPage;
